import { useQuery } from "@tanstack/react-query";

import { Input } from "@/components/ui/input";
import { useLogoReadUrl } from "@/hooks/use-logo-read-url";
import { isHexColor, PLATFORM_DEFAULT_COLOR } from "@/lib/brand";
import { sampleLogoColours } from "@/lib/logo-image";
import type { TFunction } from "@/i18n/catalog";

/** The picker has no empty state, so it shows the platform default while the field is empty or
 * half-typed. Upper case because that is what the placeholder and the API's examples use. */
const PICKER_FALLBACK = PLATFORM_DEFAULT_COLOR.toUpperCase();

/** The organisation's brand colour: picker, hex field, and the colours its logo suggests
 * (POS2-1132 built it, POS2-1149 moved it here and added the swatches).
 *
 * **Why it moved.** `/admin/organisations` had this and `/settings/organisation` did not — the
 * customer surface carried a bare text input beside a `div` whose colour read the *server* value,
 * so typing a new hex moved nothing until Save. That is POS2-1146's own pattern left half-finished:
 * it extracted `BrandLogoField` so both surfaces shared one implementation and did not do the same
 * for the field sitting next to it. So the "live preview" this ticket asked for is reuse.
 *
 * `t` is passed in for the reason `BrandLogoField` documents: `/admin/*` is English-only
 * (POS2-1133 pinned decision 4) and passes its fixed `englishT`, while `/settings/organisation`
 * passes the reader's own.
 */
export function BrandColourField({
  t,
  id,
  value,
  onChange,
  error,
  logoKey,
}: {
  t: TFunction;
  id: string;
  value: string;
  onChange: (hex: string) => void;
  error?: string;
  logoKey: string | null;
}) {
  const suggestions = useLogoColours(t, logoKey);

  return (
    <div>
      <label className="text-sm font-medium" htmlFor={id}>
        {t("settings.brandColour")}
      </label>
      <div className="mt-1 flex items-center gap-2">
        <input
          type="color"
          aria-label={t("settings.brandColourPick")}
          className="size-9 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1"
          value={isHexColor(value) ? value : PICKER_FALLBACK}
          onChange={(e) => onChange(e.target.value)}
        />
        <Input
          id={id}
          placeholder={PICKER_FALLBACK}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
      {/* Offered, never applied (POS2-1149). A dominant colour is a guess about intent — a mark
          that is mostly white with a small red glyph would auto-fill white — so the stored value
          stays one a human picked, and a bad extraction is a swatch nobody clicks. */}
      {suggestions.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t("settings.brandColourFromLogo")}</span>
          {suggestions.map((hex) => (
            <button
              key={hex}
              type="button"
              aria-label={t("settings.brandColourUse", { hex })}
              title={hex}
              className="size-6 rounded-md border border-input"
              style={{ backgroundColor: hex }}
              onClick={() => onChange(hex)}
            />
          ))}
        </div>
      )}
      <p className="mt-1 text-xs text-muted-foreground">{t("settings.brandColourHint")}</p>
    </div>
  );
}

/** The logo's usable colours, or nothing.
 *
 * Two queries rather than one because they cache on different things: the read URL is shared with
 * the logo preview beside this field, so it is already warm, while the sampling is keyed on the URL
 * so a replaced logo re-samples and an unchanged one does not. An empty list is an ordinary answer
 * — a black-and-white mark contains no colour to offer.
 */
function useLogoColours(t: TFunction, logoKey: string | null): string[] {
  const readUrl = useLogoReadUrl(t, logoKey);
  const url = readUrl.data ?? null;

  const sampled = useQuery({
    queryKey: ["logo-colours", url],
    enabled: !!url,
    staleTime: Infinity,
    queryFn: () => {
      if (!url) throw new Error("useLogoColours ran without a logo URL");
      return sampleLogoColours(url);
    },
  });

  return sampled.data ?? [];
}
